import Papa from 'papaparse';
import { readSheet } from 'read-excel-file/browser';
import writeXlsxFile from 'write-excel-file/browser';

export interface ParsedRow {
  name: string;
  role?: string;
  party?: string;
  email?: string;
  notes?: string;
  raw: string;
  lineNumber: number;
}

// Backend accepts up to 500 speakers per bulk request.
export const BULK_MAX_ROWS = 500;

const FIELD_ALIASES: Record<
  keyof Omit<ParsedRow, 'raw' | 'lineNumber'>,
  string[]
> = {
  name: ['name', 'speaker', 'full name', 'fullname'],
  role: ['role', 'title', 'position'],
  party: ['party', 'affiliation', 'group'],
  email: ['email', 'e-mail', 'mail'],
  notes: ['notes', 'note', 'comment', 'comments']
};

const normaliseHeader = (h: string): string =>
  h.trim().toLowerCase().replace(/[_-]+/g, ' ');

// Map a header row to indices for each ParsedRow field. Returns null when the
// header row doesn't contain a "name" column (or an alias) — the caller then
// falls back to positional parsing so users who omit headers still work.
const buildHeaderMap = (
  headerRow: string[]
): Record<keyof typeof FIELD_ALIASES, number> | null => {
  const normalised = headerRow.map(normaliseHeader);
  const map: Partial<Record<keyof typeof FIELD_ALIASES, number>> = {};
  for (const field of Object.keys(
    FIELD_ALIASES
  ) as (keyof typeof FIELD_ALIASES)[]) {
    const aliases = FIELD_ALIASES[field];
    const idx = normalised.findIndex(cell => aliases.includes(cell));
    if (idx >= 0) map[field] = idx;
  }
  if (map.name === undefined) return null;
  return {
    name: map.name,
    role: map.role ?? -1,
    party: map.party ?? -1,
    email: map.email ?? -1,
    notes: map.notes ?? -1
  };
};

const cellAt = (row: unknown[], idx: number): string => {
  if (idx < 0) return '';
  const v = row[idx];
  if (v === null || v === undefined) return '';
  return String(v).trim();
};

// Turn a 2D matrix (from CSV or XLSX) into ParsedRow[]. Uses the first row as
// a header if it contains "name" (or an alias); otherwise falls back to
// positional columns: name, role, party, email, notes.
export const rowsToParsedSpeakers = (rows: unknown[][]): ParsedRow[] => {
  const nonEmpty = rows.filter(r => r.some(c => String(c ?? '').trim() !== ''));
  if (nonEmpty.length === 0) return [];

  const headerCells = nonEmpty[0].map(c => String(c ?? ''));
  const headerMap = buildHeaderMap(headerCells);

  const dataRows = headerMap ? nonEmpty.slice(1) : nonEmpty;

  return dataRows.map((row, i) => {
    const name = headerMap ? cellAt(row, headerMap.name) : cellAt(row, 0);
    const role = headerMap ? cellAt(row, headerMap.role) : cellAt(row, 1);
    const party = headerMap ? cellAt(row, headerMap.party) : cellAt(row, 2);
    const email = headerMap ? cellAt(row, headerMap.email) : cellAt(row, 3);
    const notes = headerMap ? cellAt(row, headerMap.notes) : cellAt(row, 4);

    return {
      name,
      role: role || undefined,
      party: party || undefined,
      email: email || undefined,
      notes: notes || undefined,
      raw: row.map(c => String(c ?? '')).join(', '),
      lineNumber: (headerMap ? 2 : 1) + i
    };
  });
};

export const parseCsvFile = (file: File): Promise<ParsedRow[]> =>
  new Promise((resolve, reject) => {
    Papa.parse<string[]>(file, {
      skipEmptyLines: true,
      complete: results => {
        if (results.errors && results.errors.length > 0) {
          reject(new Error(results.errors[0].message));
          return;
        }
        resolve(rowsToParsedSpeakers(results.data));
      },
      error: err => reject(err)
    });
  });

export const parseXlsxFile = async (file: File): Promise<ParsedRow[]> => {
  // readSheet defaults to the first sheet and returns Row[] directly.
  const rows = await readSheet(file);
  return rowsToParsedSpeakers(rows as unknown[][]);
};

// Fallback: parse the freeform text-paste input using the same simple
// delimiter heuristics the modal used before file upload was added. Keeps
// existing text-paste behaviour working for users who prefer it.
export const parseTextInput = (text: string): ParsedRow[] => {
  if (!text.trim()) return [];
  const lines = text.split('\n').filter(l => l.trim());
  return lines.map((line, index) => {
    const trimmed = line.trim();
    if (trimmed.includes(',')) {
      const parts = trimmed.split(',').map(p => p.trim());
      // "Name, PARTY" with exactly two parts and an abbreviation-like second
      // value routes to party (e.g. "Sharon Dijksma, VVD"). Three or more parts
      // always use positional order: name, role, party, email, notes.
      if (
        parts.length === 2 &&
        (/^[A-Z]{2,10}$/.test(parts[1]) || /^[A-Z][a-z]+$/.test(parts[1]))
      ) {
        return {
          name: parts[0] || '',
          party: parts[1] || undefined,
          raw: trimmed,
          lineNumber: index + 1
        };
      }
      return {
        name: parts[0] || '',
        role: parts[1] || undefined,
        party: parts[2] || undefined,
        email: parts[3] || undefined,
        notes: parts[4] || undefined,
        raw: trimmed,
        lineNumber: index + 1
      };
    }
    if (trimmed.includes('|')) {
      const parts = trimmed.split('|').map(p => p.trim());
      return {
        name: parts[0] || '',
        role: parts[1] || undefined,
        party: parts[2] || undefined,
        email: parts[3] || undefined,
        notes: parts[4] || undefined,
        raw: trimmed,
        lineNumber: index + 1
      };
    }
    if (trimmed.includes('\t')) {
      const parts = trimmed.split('\t').map(p => p.trim());
      return {
        name: parts[0] || '',
        role: parts[1] || undefined,
        party: parts[2] || undefined,
        email: parts[3] || undefined,
        notes: parts[4] || undefined,
        raw: trimmed,
        lineNumber: index + 1
      };
    }
    // "Name Party" — last word is party if it looks like an abbreviation.
    const words = trimmed.split(/\s+/);
    if (words.length >= 2) {
      const last = words[words.length - 1];
      if (/^[A-Z]{2,10}$/.test(last) || /^[A-Z][a-z]+$/.test(last)) {
        return {
          name: words.slice(0, -1).join(' '),
          party: last,
          raw: trimmed,
          lineNumber: index + 1
        };
      }
    }
    return { name: trimmed, raw: trimmed, lineNumber: index + 1 };
  });
};

// Simple RFC-5322-ish check, matched to backend max_length=255.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface RowIssue {
  severity: 'error' | 'warning';
  message: string;
}

export const validateRow = (
  row: ParsedRow,
  t: (k: string) => string
): RowIssue[] => {
  const issues: RowIssue[] = [];
  if (!row.name) {
    issues.push({ severity: 'error', message: t('speaker-name-is-required') });
  } else if (row.name.length > 255) {
    issues.push({ severity: 'error', message: t('speaker-name-max-length') });
  }
  if (row.role && row.role.length > 255) {
    issues.push({ severity: 'error', message: t('speaker-role-max-length') });
  }
  if (row.party && row.party.length > 255) {
    issues.push({ severity: 'error', message: t('speaker-party-max-length') });
  }
  if (row.email) {
    if (row.email.length > 255 || !EMAIL_RE.test(row.email)) {
      issues.push({ severity: 'error', message: t('invalid-email') });
    }
  }
  return issues;
};

// Client-side template download — generates an .xlsx workbook with a header
// row and one example row so users can fill it in Excel and upload it back.
export const downloadXlsxTemplate = async (
  t: (k: string) => string
): Promise<void> => {
  const data = [
    ['name', 'role', 'party', 'email', 'notes'],
    [
      t('bulk-upload-template-example-name'),
      t('bulk-upload-template-example-role'),
      t('bulk-upload-template-example-party'),
      t('bulk-upload-template-example-email'),
      t('bulk-upload-template-example-notes')
    ]
  ];
  const result = await writeXlsxFile(data as string[][]);
  await result.toFile('speakers-template.xlsx');
};
